import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home,
  FileText,
  FileBarChart,
  BookOpen,
  MessageSquare,
  HelpCircle,
  User,
  Settings,
  LogOut,
  X,
  ChevronLeft
} from 'lucide-react';
import { Logo } from '../common/Logo';

export interface MainSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail: string | null;
  isAnonymous: boolean;
  onSignOut: () => void;
  onSignIn: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  alwaysOverlay?: boolean;
  className?: string;
}

export const MainSidebar: React.FC<MainSidebarProps> = ({
  isOpen,
  onClose,
  userEmail,
  isAnonymous,
  onSignOut,
  onSignIn,
  isCollapsed = false,
  onToggleCollapse,
  alwaysOverlay = false,
  className = '',
}) => {
  const navigate = useNavigate();

  return (
    <>
      {/* Overlay backdrop */}
      {isOpen && (
        <div aria-hidden="true"
          className={`fixed inset-0 bg-black/50 z-40 ${alwaysOverlay ? '' : 'lg:hidden'}`}
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed ${alwaysOverlay ? '' : 'lg:sticky'} top-0 left-0 h-screen
          w-80 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${alwaysOverlay ? 'z-50' : 'z-50 lg:z-0'}
          flex flex-col
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${alwaysOverlay ? '' : (!isCollapsed ? 'lg:translate-x-0' : 'lg:-translate-x-full')}
          ${className}
        `}
      >
        {/* Header with Logo */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <Logo onClick={onClose} />
            <div className="flex items-center space-x-1">
              {/* Collapse button - Desktop only (non-overlay mode) */}
              {onToggleCollapse && !alwaysOverlay && (
                <button
                  onClick={onToggleCollapse}
                  className="hidden lg:block p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                  title="Collapse sidebar"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {/* Close button */}
              <button
                onClick={onClose}
                className={`p-2 rounded-lg hover:bg-gray-100 text-gray-600 ${alwaysOverlay ? '' : 'lg:hidden'}`}
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-1">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-3">
              Navigation
            </h3>
            
            <Link
              to="/"
              onClick={onClose}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
            >
              <Home className="w-5 h-5 text-gray-600 group-hover:text-green-600" />
              <span className="text-sm font-medium">Home</span>
            </Link>

            <Link
              to="/library"
              onClick={onClose}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
            >
              <FileBarChart className="w-5 h-5 text-gray-600 group-hover:text-green-600" />
              <span className="text-sm font-medium">Library</span>
            </Link>

            <Link
              to="/library?tab=summaries"
              onClick={onClose}
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
            >
              <FileText className="w-5 h-5 text-gray-600 group-hover:text-green-600" />
              <span className="text-sm font-medium">Summaries</span>
            </Link>
          </div>

          {/* Resources Section */}
          <div className="p-4 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-3">
              Resources
            </h3>
            <div className="space-y-1">
              <Link
                to="/docs"
                onClick={onClose}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
              >
                <BookOpen className="w-4 h-4 text-gray-600 group-hover:text-green-600" />
                <span className="text-sm">Documentation</span>
              </Link>
              
              <Link
                to="/docs/releases"
                onClick={onClose}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
              >
                <FileText className="w-4 h-4 text-gray-600 group-hover:text-blue-600" />
                <span className="text-sm">Release Notes</span>
              </Link>
              
              <Link
                to="/feedback"
                onClick={onClose}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
              >
                <MessageSquare className="w-4 h-4 text-gray-600 group-hover:text-purple-600" />
                <span className="text-sm">Give Feedback</span>
              </Link>
              
              <Link
                to="/support"
                onClick={onClose}
                className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors group"
              >
                <HelpCircle className="w-4 h-4 text-gray-600 group-hover:text-orange-600" />
                <span className="text-sm">Help & Support</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Account Section */}
        <div className="border-t border-gray-200 p-4 mt-auto">
          {isAnonymous ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  navigate('/pricing');
                  onClose();
                }}
                className="w-full px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/30 rounded-lg text-purple-700 hover:text-purple-800 transition-colors duration-200 shadow-sm text-sm font-medium"
              >
                Pricing
              </button>
              <button
                onClick={() => {
                  onSignIn();
                  onClose();
                }}
                className="w-full px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-400/30 rounded-lg text-green-700 hover:text-green-800 transition-colors duration-200 shadow-sm text-sm font-medium"
              >
                Sign In
              </button>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-3">
                {userEmail && (
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-black text-sm font-medium truncate">
                      {userEmail}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="space-y-1">
                <button
                  onClick={() => {
                    navigate('/account');
                    onClose();
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-left"
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-sm">Account Settings</span>
                </button>
                
                <button
                  onClick={() => {
                    onSignOut();
                    onClose();
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-left"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

